import {useDispatch, useSelector} from "react-redux";
import useToast from "../../../util/useToast";
import {useCallback, useEffect, useState} from "react";
import {AMOUNT_REGEX, MILEAGE_LOG_ODOMETER_REGEX, MILEAGE_LOG_QUANTITY_REGEX, MODE_EDIT} from "../../../codes";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {selectAllCars} from "../../../slices/carsSlice";
import {
  editorConfigSelector,
  listConfigSelector,
  rxHideMileageLogEditor, rxShowHideDeleteConfirmationModal,
  rxUpdateMileageLogs
} from "../../../slices/mileageLogsSlice";
import {createMileageLog, deleteMileageLog, updateMileageLog} from "../../../api/ApiMileageLogs";
import {getOrDefault} from "../../../util/util";
import {selectAllAccounts} from "../../../slices/accountsSlice";
import {appConfigSelector} from "../../../slices/appSlice";
import {selectTransactionById} from "../../../slices/transactionsSlice";
import {selectAllCategories} from "../../../slices/categoriesSlice";
import useNetwork from "../../../util/useNetwork";
import useValidator from "../../../util/useValidator";

const useMileageLogEditor = () => {
  const dispatch = useDispatch();

  const {t} = useTranslation("cars");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const cars = useSelector(selectAllCars);
  const accounts = useSelector(selectAllAccounts);
  const transactionCategories = useSelector(selectAllCategories);

  const {selectedCarId} = useSelector(listConfigSelector);
  const {show, mode, mileageLog, deleteConfirmationModal} = useSelector(editorConfigSelector);
  const {defaultCarsAccount, defaultCarsTransactionCategory} = useSelector(appConfigSelector);

  const mileageLogTransaction = useSelector(s => selectTransactionById(s, mileageLog?.transaction));

  const [editedMileageLog, setEditedMileageLog] = useState({errors: {}, odometer: "", quantity: "", price: ""});
  const [availableCars, setAvailableCars] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  const hideMileageLogEditor = () => dispatch(rxHideMileageLogEditor());

  useEffect(() => {
    if (!mileageLog || !show) {
      return;
    }

    const values = {
      errors: {},
      car: getOrDefault(mileageLog.car, selectedCarId),
      account: mode === MODE_EDIT ? getOrDefault(mileageLogTransaction?.account, null) : defaultCarsAccount,
      category: mode === MODE_EDIT ? getOrDefault(mileageLogTransaction?.category, null) : defaultCarsTransactionCategory,
      odometer: "" + getOrDefault(mileageLog.odometer, ""),
      quantity: "" + getOrDefault(mileageLog.quantity, ""),
      price: "" + getOrDefault(mileageLog.price, ""),
      full: getOrDefault(mileageLog.full, true),
      valueDate: getOrDefault(mileageLog.valueDate, moment().format("YYYY-MM-DD"))
    };

    setEditedMileageLog(values);

    setAvailableCars(() => cars.filter(c => c.active || c.id === values.car));

    const res = [{id: null, name: t("mileage_log_editor_no_account_option")}]

    accounts.filter(a => a.active || a.id === defaultCarsAccount).forEach(a => res.push(a));

    setAvailableAccounts(res);

    setAvailableCategories(() => transactionCategories.filter(c => c.active || c.id === defaultCarsTransactionCategory));
  }, [show, mode, mileageLog, mileageLogTransaction, cars, accounts, transactionCategories, defaultCarsAccount, defaultCarsTransactionCategory, selectedCarId, t]);

  const getErrors = values => {
    const errors = {};

    if (values.odometer.trim() === "") {
      errors.odometer = "mileage_log_editor_error_odometer_must_be_filled";
    }

    if (values.quantity === undefined) {
      errors.quantity = "mileage_log_editor_error_quantity_must_be_filled";
    }

    if (values.price === undefined) {
      errors.price = "mileage_log_editor_error_price_must_be_filled";
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    let quantity, price;

    if (editedMileageLog.quantity.length) {
      quantity = +editedMileageLog.quantity.replace(",", ".");
    }

    if (editedMileageLog.price.length) {
      price = +editedMileageLog.price.replace(",", ".");
    }

    const values = {
      car: editedMileageLog.car,
      account: editedMileageLog.account,
      category: editedMileageLog.category,
      odometer: editedMileageLog.odometer,
      quantity: quantity,
      price: price,
      valueDate: editedMileageLog.valueDate,
      full: editedMileageLog.full
    };

    return validate(getErrors(values))
      .then(() => {
        if (mode === MODE_EDIT) {
          return updateMileageLog(mileageLog.id, values);
        } else {
          return createMileageLog(values);
        }
      })
      .then(result => dispatch(rxUpdateMileageLogs(result)))
      .then(() => {
        showToastSuccess(t(mode === MODE_EDIT ? "mileage_log_edited" : "mileage_log_added"));

        hideMileageLogEditor();
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedMileageLog(prev => ({...prev, errors: err.response.data}))
        });
      });
  };

  const updateEditedMileageLog = field => event => {
    setEditedMileageLog(prev => {
      const values = {};

      switch (field) {
        case "account":
        case "category":
        case "car":
        case "valueDate":
        case "full":
          values[field] = event;
          break;
        case "odometer": {
          const odometer = event.target.value;

          if (MILEAGE_LOG_ODOMETER_REGEX.test(odometer)) {
            values.odometer = odometer;
          }

          break;
        }
        case "quantity": {
          const quantity = event.target.value;

          if (MILEAGE_LOG_QUANTITY_REGEX.test(quantity)) {
            values.quantity = quantity;
          }

          break;
        }
        case "price": {
          const price = event.target.value;

          if (AMOUNT_REGEX.test(price)) {
            values.price = price;
          }

          break;
        }
        default:
          values[field] = event.target.value;
          break;
      }

      return ({...prev, ...values});
    });
  };

  const showDeleteConfirmationModal = useCallback((() => dispatch(rxShowHideDeleteConfirmationModal(true))), [dispatch]);
  const hideDeleteConfirmationModal = useCallback((() => dispatch(rxShowHideDeleteConfirmationModal(false))), [dispatch]);

  const onDeleteMileageLog = () => {
    return deleteMileageLog(mileageLog.id)
      .then(result => {
        dispatch(rxUpdateMileageLogs(result));
        dispatch(rxHideMileageLogEditor());
      })
      .catch(handleNetworkError)
      .finally(hideDeleteConfirmationModal);
  };

  return {
    show,
    t,
    mode,
    submit,
    hideMileageLogEditor,
    mileageLog,
    editedMileageLog,
    updateEditedMileageLog,
    availableCars,
    availableAccounts,
    availableCategories,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteMileageLog
  };
};

export default useMileageLogEditor;
